// Canonical definition lives in `@skaile/workspaces/types` — it is the wire
// shape persisted on `CheckEvidence`/`NodeExecutionAttempt`, not a connector-
// local concept. Re-exported here so existing in-connector imports are unaffected.
import type { BindingOrigin } from "@skaile/workspaces/types";
export type { BindingOrigin };

/** Hard resource budgets applied to every Flow expression evaluation. */
export const FLOW_EXPRESSION_LIMITS = Object.freeze({
  maxLength: 4096,
  maxTokens: 256,
  maxNesting: 32,
  maxPathSegments: 32,
  maxCollectionItems: 1024,
});

/** Read-only values available to the bounded Flow expression language. */
export interface FlowExpressionContext {
  /** Validated input supplied to the current flow run. */
  flow: { input: unknown };
  /** Authored defaults exposed through bounded own-property reads. */
  defaults?: Record<string, unknown>;
  /** Node status and declared output fields keyed by node identifier. */
  nodes: Record<string, { status?: string; output?: { fields?: unknown } }>;
}

/** A resolved JSON value together with every source that contributed to it. */
export interface ResolvedBinding {
  /** Runtime value passed to the node executor. */
  value: unknown;
  /** Stable, de-duplicated provenance in authored traversal order. */
  origins: BindingOrigin[];
}

/** All named node inputs, keeping executor values separate from provenance. */
export interface ResolvedNodeBindings {
  /** Resolved values keyed by authored binding name. */
  values: Record<string, unknown>;
  /** Provenance keyed by authored binding name. */
  origins: Record<string, BindingOrigin[]>;
}

/** How each producer node obtained its output fields. */
export type NodeOutputOrigin = Record<string, "agent" | "runtime">;

/** @internal Binding names excluded from both authored and direct typed inputs. */
export const FLOW_BINDING_NAME_PATTERN = /^(?!__proto__$)/;

/** @internal Collect bounded expressions embedded in a JSON binding value. */
export function collectFlowBindingExpressions(value: unknown): string[] {
  if (typeof value === "string") {
    return Array.from(value.matchAll(/\$\{([^{}]+)\}/g), (match) => match[1]);
  }
  if (Array.isArray(value)) return value.flatMap(collectFlowBindingExpressions);
  if (typeof value !== "object" || value === null) return [];
  return Object.values(value).flatMap(collectFlowBindingExpressions);
}

/** Deterministic syntax, budget, or path failure raised by the Flow evaluator. */
export class FlowExpressionError extends Error {
  /** Stable error name for callers that classify evaluator failures. */
  override readonly name = "FlowExpressionError";

  /** Runtime path that was grammar-valid but absent from the supplied context. */
  readonly missingPath?: string;

  /** Producer node whose `skipped` status is why {@link missingPath} is absent. */
  readonly skippedProducer?: string;

  constructor(message: string, missingPath?: string, skippedProducer?: string) {
    super(message);
    this.missingPath = missingPath;
    this.skippedProducer = skippedProducer;
  }
}

type TokenKind = "value" | "word" | "operator" | "punctuation" | "eof";
interface Token {
  kind: TokenKind;
  value: unknown;
  position: number;
}

function expressionError(
  message: string,
  position?: number,
  missingPath?: string,
  skippedProducer?: string,
): FlowExpressionError {
  return new FlowExpressionError(
    position === undefined
      ? `Flow expression ${message}`
      : `Flow expression ${message} at ${position}`,
    missingPath,
    skippedProducer,
  );
}

function parseString(source: string, start: number): { value: string; end: number } {
  const quote = source[start];
  let value = "";
  let index = start + 1;
  while (index < source.length) {
    const character = source[index];
    if (character === quote) return { value, end: index + 1 };
    if (character !== "\\") {
      value += character;
      index++;
      continue;
    }

    const escaped = source[index + 1];
    if (escaped === undefined) throw expressionError("has an unterminated string", start);
    const replacements: Record<string, string> = {
      "\\": "\\",
      "'": "'",
      '"': '"',
      n: "\n",
      r: "\r",
      t: "\t",
      b: "\b",
      f: "\f",
    };
    if (escaped === "u") {
      const hex = source.slice(index + 2, index + 6);
      if (!/^[0-9a-fA-F]{4}$/.test(hex)) {
        throw expressionError("has an invalid unicode escape", index);
      }
      value += String.fromCharCode(Number.parseInt(hex, 16));
      index += 6;
      continue;
    }
    if (!(escaped in replacements)) throw expressionError("has an invalid escape", index);
    value += replacements[escaped];
    index += 2;
  }
  throw expressionError("has an unterminated string", start);
}

function tokenize(source: string): Token[] {
  if (source.length > FLOW_EXPRESSION_LIMITS.maxLength) {
    throw expressionError(`exceeds the length limit of ${FLOW_EXPRESSION_LIMITS.maxLength}`);
  }

  const tokens: Token[] = [];
  const add = (token: Token): void => {
    tokens.push(token);
    if (tokens.length > FLOW_EXPRESSION_LIMITS.maxTokens) {
      throw expressionError(`exceeds the token limit of ${FLOW_EXPRESSION_LIMITS.maxTokens}`);
    }
  };

  let index = 0;
  while (index < source.length) {
    const character = source[index];
    if (/\s/.test(character)) {
      index++;
      continue;
    }

    const pair = source.slice(index, index + 2);
    if (["&&", "||", "==", "!=", "<=", ">="].includes(pair)) {
      add({ kind: "operator", value: pair, position: index });
      index += 2;
      continue;
    }
    if (["!", "<", ">"].includes(character)) {
      add({ kind: "operator", value: character, position: index });
      index++;
      continue;
    }
    if (["(", ")", "[", "]", ","].includes(character)) {
      add({ kind: "punctuation", value: character, position: index });
      index++;
      continue;
    }
    if (character === "'" || character === '"') {
      const parsed = parseString(source, index);
      add({ kind: "value", value: parsed.value, position: index });
      index = parsed.end;
      continue;
    }

    const number = source.slice(index).match(/^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?/);
    if (number) {
      add({ kind: "value", value: Number(number[0]), position: index });
      index += number[0].length;
      continue;
    }

    const word = source.slice(index).match(/^[A-Za-z_][A-Za-z0-9_.-]*/);
    if (word) {
      const raw = word[0];
      if (raw === "true") add({ kind: "value", value: true, position: index });
      else if (raw === "false") add({ kind: "value", value: false, position: index });
      else if (raw === "null") add({ kind: "value", value: null, position: index });
      else if (raw === "in") add({ kind: "operator", value: raw, position: index });
      else add({ kind: "word", value: raw, position: index });
      index += raw.length;
      continue;
    }

    throw expressionError(`contains unsupported token ${JSON.stringify(character)}`, index);
  }
  tokens.push({ kind: "eof", value: "", position: source.length });
  return tokens;
}

const FORBIDDEN_PATH_SEGMENTS = new Set(["__proto__", "prototype", "constructor"]);

function ownValue(value: unknown, segment: string, position: number, path: string): unknown {
  if ((typeof value !== "object" && typeof value !== "function") || value === null) {
    throw expressionError("path does not exist", position, path);
  }
  if (!Object.hasOwn(value, segment)) {
    throw expressionError("path does not exist", position, path);
  }
  return (value as Record<string, unknown>)[segment];
}

function resolvePath(
  raw: string,
  context: FlowExpressionContext,
  position: number,
  traverse: boolean,
): unknown {
  const segments = raw.split(".");
  if (segments.length > FLOW_EXPRESSION_LIMITS.maxPathSegments) {
    throw expressionError(
      `exceeds the path traversal limit of ${FLOW_EXPRESSION_LIMITS.maxPathSegments}`,
      position,
    );
  }
  if (segments.some((segment) => segment.length === 0)) {
    throw expressionError("contains an empty path segment", position);
  }
  const forbidden = segments.find((segment) => FORBIDDEN_PATH_SEGMENTS.has(segment));
  if (forbidden) throw expressionError(`contains forbidden path segment ${forbidden}`, position);

  let value: unknown;
  let remaining: string[];
  /** Producer id when this read is an output read over a node that was skipped. */
  let skippedProducer: string | undefined;
  if (segments[0] === "flow" && segments[1] === "input") {
    if (segments.length === 2) {
      throw expressionError("path requires a JSON property suffix", position);
    }
    value = context.flow.input;
    remaining = segments.slice(2);
  } else if (segments[0] === "defaults" && segments.length >= 2) {
    value = context.defaults ?? {};
    remaining = segments.slice(1);
  } else if (segments[0] === "nodes" && segments.length === 3 && segments[2] === "status") {
    value = context.nodes;
    remaining = segments.slice(1);
  } else if (
    segments[0] === "nodes" &&
    segments.length >= 4 &&
    segments[2] === "output" &&
    segments[3] === "fields"
  ) {
    if (segments.length === 4) {
      throw expressionError("path requires a JSON property suffix", position);
    }
    if (
      Object.hasOwn(context.nodes, segments[1]) &&
      context.nodes[segments[1]].status === "skipped"
    ) {
      skippedProducer = segments[1];
    }
    value = context.nodes;
    remaining = segments.slice(1);
  } else {
    throw expressionError(`contains unsupported path ${raw}`, position);
  }

  if (!traverse) return undefined;
  // Raised only on the evaluating pass so a short-circuited branch stays inert.
  // A skipped producer's output is discarded before the context is built, so
  // without this the read reports the generic "path does not exist".
  if (skippedProducer !== undefined) {
    throw expressionError(
      `cannot read ${raw}: producer node ${skippedProducer} was skipped and produced no output`,
      position,
      raw,
      skippedProducer,
    );
  }
  for (const segment of remaining) value = ownValue(value, segment, position, raw);
  return value;
}

function compare(operator: string, left: unknown, right: unknown, position: number): boolean {
  if (operator === "==") return Object.is(left, right);
  if (operator === "!=") return !Object.is(left, right);
  if (operator === "in") {
    if (!Array.isArray(right))
      throw expressionError("requires an array on the right of in", position);
    if (right.length > FLOW_EXPRESSION_LIMITS.maxCollectionItems) {
      throw expressionError(
        `exceeds the collection item limit of ${FLOW_EXPRESSION_LIMITS.maxCollectionItems}`,
        position,
      );
    }
    return right.some((candidate) => Object.is(candidate, left));
  }
  if (
    !(
      (typeof left === "number" && typeof right === "number") ||
      (typeof left === "string" && typeof right === "string")
    )
  ) {
    throw expressionError(`requires comparable operands for ${operator}`, position);
  }
  if (operator === "<") return left < right;
  if (operator === "<=") return left <= right;
  if (operator === ">") return left > right;
  if (operator === ">=") return left >= right;
  throw expressionError(`contains unsupported operator ${operator}`, position);
}

const STATIC_BOOLEAN = Symbol("static-boolean");
const STATIC_PATH = Symbol("static-path");

interface StaticPath {
  marker: typeof STATIC_PATH;
  path: string;
}

function isStaticPath(value: unknown): value is StaticPath {
  return (
    typeof value === "object" && value !== null && (value as StaticPath).marker === STATIC_PATH
  );
}

function isBooleanOperand(value: unknown): boolean {
  return typeof value === "boolean" || value === STATIC_BOOLEAN || isStaticPath(value);
}

function assertBooleanOperand(value: unknown, position: number): void {
  if (!isBooleanOperand(value)) throw expressionError("requires boolean operands", position);
}

class Parser {
  private index = 0;
  private nesting = 0;

  constructor(
    private readonly tokens: Token[],
    private readonly context: FlowExpressionContext,
    private readonly onPath?: (path: string) => void,
    private readonly onBooleanPath?: (path: string) => void,
    private readonly onEvaluatedPath?: (path: string) => void,
  ) {}

  parse(evaluate = true): unknown {
    const value = this.parseOr(evaluate);
    const trailing = this.peek();
    if (trailing.kind !== "eof") {
      throw expressionError(
        `contains unexpected token ${String(trailing.value)}`,
        trailing.position,
      );
    }
    return value;
  }

  private peek(): Token {
    return this.tokens[this.index];
  }

  private take(): Token {
    return this.tokens[this.index++];
  }

  private accept(value: string): Token | undefined {
    if (this.peek().value !== value) return undefined;
    return this.take();
  }

  private parseOr(evaluate: boolean): unknown {
    let value = this.parseAnd(evaluate);
    while (this.peek().value === "||") {
      const operator = this.take();
      assertBooleanOperand(value, operator.position);
      if (isStaticPath(value)) this.onBooleanPath?.(value.path);
      const evaluateRight = evaluate && value === false;
      const right = this.parseAnd(evaluateRight);
      assertBooleanOperand(right, operator.position);
      if (isStaticPath(right)) this.onBooleanPath?.(right.path);
      value = evaluate ? value === true || right === true : STATIC_BOOLEAN;
    }
    return value;
  }

  private parseAnd(evaluate: boolean): unknown {
    let value = this.parseComparison(evaluate);
    while (this.peek().value === "&&") {
      const operator = this.take();
      assertBooleanOperand(value, operator.position);
      if (isStaticPath(value)) this.onBooleanPath?.(value.path);
      const evaluateRight = evaluate && value === true;
      const right = this.parseComparison(evaluateRight);
      assertBooleanOperand(right, operator.position);
      if (isStaticPath(right)) this.onBooleanPath?.(right.path);
      value = evaluate ? value === true && right === true : STATIC_BOOLEAN;
    }
    return value;
  }

  private parseComparison(evaluate: boolean): unknown {
    const left = this.parseUnary(evaluate);
    const token = this.peek();
    if (
      token.kind !== "operator" ||
      !["==", "!=", "<", "<=", ">", ">=", "in"].includes(String(token.value))
    ) {
      return left;
    }
    this.take();
    const right = this.parseUnary(evaluate);
    return evaluate ? compare(String(token.value), left, right, token.position) : STATIC_BOOLEAN;
  }

  private parseUnary(evaluate: boolean): unknown {
    const operator = this.accept("!");
    if (operator) {
      const value = this.parseUnary(evaluate);
      assertBooleanOperand(value, operator.position);
      if (isStaticPath(value)) this.onBooleanPath?.(value.path);
      return evaluate ? value === false : STATIC_BOOLEAN;
    }
    return this.parsePrimary(evaluate);
  }

  private parsePrimary(evaluate: boolean): unknown {
    const token = this.take();
    if (token.kind === "value") return token.value;
    if (token.kind === "word") {
      this.onPath?.(String(token.value));
      resolvePath(String(token.value), this.context, token.position, false);
      if (evaluate) this.onEvaluatedPath?.(String(token.value));
      return evaluate
        ? resolvePath(String(token.value), this.context, token.position, true)
        : { marker: STATIC_PATH, path: String(token.value) };
    }
    if (token.value === "(") {
      this.enterNesting(token.position);
      const value = this.parseOr(evaluate);
      if (!this.accept(")"))
        throw expressionError("is missing a closing parenthesis", token.position);
      this.nesting--;
      return value;
    }
    if (token.value === "[") {
      this.enterNesting(token.position);
      const values: unknown[] = [];
      if (!this.accept("]")) {
        do values.push(this.parsePrimary(evaluate));
        while (this.accept(","));
        if (!this.accept("]"))
          throw expressionError("is missing a closing bracket", token.position);
      }
      this.nesting--;
      return values;
    }
    throw expressionError(`contains unexpected token ${String(token.value)}`, token.position);
  }

  private enterNesting(position: number): void {
    this.nesting++;
    if (this.nesting > FLOW_EXPRESSION_LIMITS.maxNesting) {
      throw expressionError(
        `exceeds the nesting limit of ${FLOW_EXPRESSION_LIMITS.maxNesting}`,
        position,
      );
    }
  }
}

/** Static path evidence used to reconcile safe grammar with declared producer output types. */
export interface FlowExpressionReference {
  /** Grammar-approved context path. */
  path: string;
  /** True when logical evaluation requires the resolved value itself to be boolean. */
  booleanOperand: boolean;
}

/** Validates authored syntax without reading runtime values; binding mode relaxes only the result type. */
export function inspectFlowExpression(
  expression: string,
  requireBoolean = true,
): FlowExpressionReference[] {
  const tokens = tokenize(expression);
  if (expression.trim() === "default") return [];
  const references = new Map<string, boolean>();
  const record = (path: string, booleanOperand: boolean): void => {
    references.set(path, (references.get(path) ?? false) || booleanOperand);
  };
  const context: FlowExpressionContext = { flow: { input: {} }, nodes: {} };
  const result = new Parser(
    tokens,
    context,
    (path) => record(path, false),
    (path) => record(path, true),
  ).parse(false);
  if (requireBoolean && typeof result !== "boolean" && result !== STATIC_BOOLEAN) {
    throw expressionError("must evaluate to a boolean");
  }
  return Array.from(references, ([path, booleanOperand]) => ({ path, booleanOperand }));
}

/**
 * Evaluates the bounded Flow expression subset without calls, arithmetic, or dynamic property access.
 * The `default` sentinel is valid only as the complete expression and matches unconditionally.
 */
export function evaluateFlowExpression(
  expression: string,
  context: FlowExpressionContext,
): boolean {
  const tokens = tokenize(expression);
  if (expression.trim() === "default") return true;
  const result = new Parser(tokens, context).parse();
  if (typeof result !== "boolean") {
    throw expressionError("must evaluate to a boolean");
  }
  return result;
}

/** Proposal-compatible short name for {@link evaluateFlowExpression}. */
export function evaluate(expression: string, context: FlowExpressionContext): boolean {
  return evaluateFlowExpression(expression, context);
}

function evaluateFlowExpressionValue(
  expression: string,
  context: FlowExpressionContext,
): { value: unknown; paths: string[] } {
  const tokens = tokenize(expression);
  if (expression.trim() === "default") return { value: true, paths: [] };
  const paths: string[] = [];
  const value = new Parser(tokens, context, undefined, undefined, (path) =>
    paths.push(path),
  ).parse();
  return { value, paths };
}

function originsForPath(
  path: string,
  nodeOutputOrigin: NodeOutputOrigin,
  flowInputOrigins: Record<string, BindingOrigin[]>,
): BindingOrigin[] {
  if (path.startsWith("flow.input.")) {
    const inputName = path.split(".")[2];
    const inherited = flowInputOrigins[inputName];
    return inherited ?? [{ source: "flow-input", path, obtainedBy: "runtime" }];
  }
  if (path.startsWith("defaults.")) {
    return [{ source: "default", path, obtainedBy: "runtime" }];
  }

  const segments = path.split(".");
  const nodeId = segments[1];
  if (segments.length === 3 && segments[0] === "nodes" && segments[2] === "status") {
    return [{ source: "node-status", nodeId, path, obtainedBy: "runtime" }];
  }
  if (
    segments.length >= 5 &&
    segments[0] === "nodes" &&
    segments[2] === "output" &&
    segments[3] === "fields"
  ) {
    const obtainedBy = nodeOutputOrigin[nodeId];
    if (!obtainedBy) {
      throw expressionError(`has no output origin for node ${nodeId}`);
    }
    return [{ source: "node-output", nodeId, path, obtainedBy }];
  }

  throw expressionError(`contains unsupported path ${path}`);
}

/** @internal Serialize a JSON-compatible value with deterministic object-key ordering. */
export function stableFlowJson(value: unknown): string {
  if (value === undefined) return "null";
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(stableFlowJson).join(",")}]`;
  const object = value as Record<string, unknown>;
  return `{${Object.keys(object)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableFlowJson(object[key])}`)
    .join(",")}}`;
}

function interpolationText(value: unknown): string {
  if (typeof value === "string") return value;
  return stableFlowJson(value);
}

function deduplicateOrigins(origins: BindingOrigin[]): BindingOrigin[] {
  const seen = new Set<string>();
  return origins.filter((origin) => {
    const key = JSON.stringify(origin);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function resolveBindingValue(
  authored: unknown,
  bindingPath: string,
  context: FlowExpressionContext,
  nodeOutputOrigin: NodeOutputOrigin,
  flowInputOrigins: Record<string, BindingOrigin[]>,
  depth: number,
): ResolvedBinding {
  if (depth > FLOW_EXPRESSION_LIMITS.maxNesting) {
    throw expressionError(`exceeds the nesting limit of ${FLOW_EXPRESSION_LIMITS.maxNesting}`);
  }

  if (typeof authored === "string") {
    const exact = authored.match(/^\$\{([^{}]+)\}$/);
    if (exact) {
      const evaluated = evaluateFlowExpressionValue(exact[1], context);
      return {
        value: evaluated.value,
        origins: deduplicateOrigins(
          evaluated.paths.flatMap((path) =>
            originsForPath(path, nodeOutputOrigin, flowInputOrigins),
          ),
        ),
      };
    }

    const matches = Array.from(authored.matchAll(/\$\{([^{}]+)\}/g));
    if (matches.length === 0) {
      return {
        value: authored,
        origins: [{ source: "literal", path: bindingPath, obtainedBy: "runtime" }],
      };
    }

    const origins: BindingOrigin[] = [
      { source: "literal", path: bindingPath, obtainedBy: "runtime" },
    ];
    const value = authored.replace(/\$\{([^{}]+)\}/g, (_placeholder, expression: string) => {
      const evaluated = evaluateFlowExpressionValue(expression, context);
      origins.push(
        ...evaluated.paths.flatMap((path) =>
          originsForPath(path, nodeOutputOrigin, flowInputOrigins),
        ),
      );
      return interpolationText(evaluated.value);
    });
    return { value, origins: deduplicateOrigins(origins) };
  }

  if (Array.isArray(authored)) {
    if (authored.length > FLOW_EXPRESSION_LIMITS.maxCollectionItems) {
      throw expressionError(
        `exceeds the collection item limit of ${FLOW_EXPRESSION_LIMITS.maxCollectionItems}`,
      );
    }
    const resolved = authored.map((value) =>
      resolveBindingValue(
        value,
        bindingPath,
        context,
        nodeOutputOrigin,
        flowInputOrigins,
        depth + 1,
      ),
    );
    return {
      value: resolved.map(({ value }) => value),
      origins: deduplicateOrigins([
        { source: "literal", path: bindingPath, obtainedBy: "runtime" },
        ...resolved.flatMap(({ origins }) => origins),
      ]),
    };
  }

  if (typeof authored === "object" && authored !== null) {
    const entries = Object.entries(authored);
    if (entries.length > FLOW_EXPRESSION_LIMITS.maxCollectionItems) {
      throw expressionError(
        `exceeds the collection item limit of ${FLOW_EXPRESSION_LIMITS.maxCollectionItems}`,
      );
    }
    const resolved = entries.map(
      ([key, value]) =>
        [
          key,
          resolveBindingValue(
            value,
            bindingPath,
            context,
            nodeOutputOrigin,
            flowInputOrigins,
            depth + 1,
          ),
        ] as const,
    );
    return {
      value: Object.fromEntries(resolved.map(([key, result]) => [key, result.value])),
      origins: deduplicateOrigins([
        { source: "literal", path: bindingPath, obtainedBy: "runtime" },
        ...resolved.flatMap(([, result]) => result.origins),
      ]),
    };
  }

  return {
    value: authored,
    origins: [{ source: "literal", path: bindingPath, obtainedBy: "runtime" }],
  };
}

/**
 * Resolves authored node inputs through the bounded Flow expression grammar.
 *
 * Exact `${...}` placeholders retain their JSON type. Placeholders embedded in
 * text use deterministic interpolation, while arrays and objects are traversed
 * recursively. Runtime values and provenance remain separate so executors do not
 * need to understand trust metadata.
 */
export function resolveFlowBindings(
  bindings: Record<string, unknown> | undefined,
  context: FlowExpressionContext,
  nodeOutputOrigin: NodeOutputOrigin = {},
  flowInputOrigins: Record<string, BindingOrigin[]> = {},
): ResolvedNodeBindings {
  const values: Record<string, unknown> = {};
  const origins: Record<string, BindingOrigin[]> = {};
  const entries = Object.entries(bindings ?? {});
  for (const [name] of entries) {
    if (!FLOW_BINDING_NAME_PATTERN.test(name)) {
      throw expressionError(`contains reserved binding name input.${name}`);
    }
  }
  for (const [name, authored] of entries) {
    const resolved = resolveBindingValue(
      authored,
      `input.${name}`,
      context,
      nodeOutputOrigin,
      flowInputOrigins,
      0,
    );
    values[name] = resolved.value;
    origins[name] = resolved.origins;
  }
  return { values, origins };
}
